import type { Row } from '@tanstack/react-table';
import { useCallback, useEffect, useRef } from 'react';
import { useThrottle } from '@/hooks/useThrottle';
import { hasClickableAncestor } from './clickableElement';

/**
 * テーブルのユーザーインタラクション（スクロール、クリック)管理フック
 * - スクロール状態の検知とスロットリング（`data-scrolling` 属性を直接更新し、re-renderを発生させない）
 * - 行クリックイベントの処理と競合回避
 * - キーボードアクセシビリティ対応
 */
interface UseTableInteractionOptions<TData> {
  /** 行クリック時のコールバック関数 */
  onRowClick: ((row: Row<TData>) => void) | null;
  /** 行ごとのクリック可否 */
  isRowClickable?: (row: Row<TData>) => boolean;
}
interface UseTableInteractionReturn<TData> {
  /** テーブルコンテナの参照 */
  containerRef: React.RefObject<HTMLDivElement>;
  /** 行クリックイベントハンドラ */
  handleRowClick: (event: React.MouseEvent<HTMLTableRowElement>, rowData: Row<TData>) => void;
}
export const useTableInteraction = <TData,>({
  onRowClick,
  isRowClickable
}: UseTableInteractionOptions<TData>): UseTableInteractionReturn<TData> => {
  const containerRef = useRef<HTMLDivElement>(null);

  // スクロール状態の検知（カラム固定時の影表示用）
  // state経由のre-renderではなく、コンテナのdata-scrolling属性を直接トグルする
  const handleThrottledScroll = useThrottle(useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    // 同値書き込みをガードし、不要なstyle再計算を避ける
    const next = String(!!container.scrollLeft);
    if (container.dataset.scrolling !== next) {
      container.dataset.scrolling = next;
    }
  }, []), 100);

  // スクロールイベントリスナーの設定・クリーンアップ
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener('scroll', handleThrottledScroll);
    return () => container.removeEventListener('scroll', handleThrottledScroll);
  }, [handleThrottledScroll]);

  // 行クリックイベントの処理
  const handleRowClick = useCallback((event: React.MouseEvent<HTMLTableRowElement>, rowData: Row<TData>) => {
    if (onRowClick === null || isRowClickable?.(rowData) === false || !(event.target instanceof HTMLElement)) {
      return;
    }

    // セル内のクリッカブル要素がクリックされた場合は行クリックを無効化
    if (hasClickableAncestor(event.target, event.currentTarget)) {
      event.stopPropagation();
      return;
    }
    onRowClick(rowData);
  }, [isRowClickable, onRowClick]);
  return {
    containerRef,
    handleRowClick
  };
};